import {
  BaseServerDisconnectReason,
  BaseWebSocketErrorCode,
  isBaseWebsocketServerErrorMessage,
  isServerDisconnectReasonMessage,
} from '@genially/p2p-lib';

import type { P2pLogger } from '../../util/logger';
import { createLogger, LoggerLevel } from '../../util/logger';
import { Transport } from './Transport';

export type TransportConnectionConfig = {
  url: string;
  roomId: string;
  targetId: string;
  cookie?: string;

  logger?: P2pLogger;

  otherParams?: Record<string, string>;
};

export class WebSocketTransport<Message = any> extends Transport<Message> {
  private readonly webSocket: WebSocket;

  protected logger: P2pLogger;

  constructor({
    url,
    roomId,
    cookie,
    targetId,
    logger,
    ...config
  }: TransportConnectionConfig) {
    super();

    this.logger =
      logger ??
      createLogger({
        minLoggerLevel: LoggerLevel.Debug,
        prefix: 'WebSocketTransport',
      });

    const queryParams = new URLSearchParams({
      roomId,
      targetId,
      cookie: cookie || '',
      ...config.otherParams,
    });

    const wsUrl = url.replace(/^http/, 'ws');

    this.webSocket = new WebSocket(`${wsUrl}?${queryParams.toString()}`);

    this.webSocket.binaryType = 'arraybuffer';

    this.setListeners();
  }

  private setListeners() {
    this.webSocket.addEventListener('error', this.handleConnectionError.bind(this));
    this.webSocket.addEventListener('close', this.handleConnectionClose.bind(this));
    this.webSocket.addEventListener('open', this.handleConnectionOpen.bind(this), {
      once: true,
    });

    this.webSocket.onmessage = event => {
      this.emitter.emit('message', { data: event.data } as Message);
    };
  }

  private removeListeners() {
    this.webSocket.removeEventListener('error', this.handleConnectionError.bind(this));
    this.webSocket.removeEventListener('close', this.handleConnectionClose.bind(this));
    this.webSocket.removeEventListener('open', this.handleConnectionOpen.bind(this));

    this.webSocket.onmessage = null;
  }

  private handleConnectionOpen = () => {
    this.emitter.emit('open');
  };

  private handleConnectionClose = (event: CloseEvent) => {
    switch (event.code) {
      case BaseWebSocketErrorCode.Disconnection:
        this.handleConnectionCloseByDisconnect(event);
        break;
      default:
        this.handleConnectionCloseByUnknownError(event);
        break;
    }
  };

  private handleConnectionCloseByDisconnect = (event: CloseEvent) => {
    let reasonAsJson: unknown;

    const metadata = {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
    };

    try {
      reasonAsJson = JSON.parse(event.reason);
    } catch (e) {
      this.logger.warn(`Connection closed with invalid reason`, metadata);

      this.emitter.emit('close', undefined);

      return;
    }

    if (!isServerDisconnectReasonMessage(reasonAsJson)) {
      this.logger.warn(`Connection closed with unknown reason`, metadata);

      this.emitter.emit('close', undefined);
    } else if (reasonAsJson.type === BaseServerDisconnectReason.Error) {
      const { error } = reasonAsJson;

      if (isBaseWebsocketServerErrorMessage(error)) {
        this.logger.debug(`Connection closed with error`, { error });

        this.emitter.emit('error', error);
      } else {
        this.logger.warn(`Connection closed with unknown error`, metadata);

        this.emitter.emit('close', undefined);
      }
    } else {
      this.emitter.emit('close', reasonAsJson);
    }
  };

  private handleConnectionCloseByUnknownError = (event: CloseEvent) => {
    const metadata = {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
    };

    this.logger.warn(`Connection closed with unknown error`, metadata);

    this.emitter.emit('close', undefined);
  };

  private handleConnectionError = (event: Event) => {
    this.logger.warn(`Unhandled connection error`, { event });

    this.emitter.emit('error', undefined);
  };

  send(message: string): void {
    this.webSocket.send(message);
  }

  close(): void {
    this.removeListeners();

    this.webSocket.close();
  }

  get connecting(): boolean {
    return this.webSocket.readyState === WebSocket.CONNECTING;
  }

  get connected(): boolean {
    return this.webSocket.readyState === WebSocket.OPEN;
  }

  get readyState(): number {
    return this.webSocket.readyState;
  }
}
